"use client";

import Link from "next/link";

// Two fills, because the button's own colour is close enough to the page's that
// the page decides whether it is visible at all.
//
// `dark` is what the CSS ships: #1a1a1a, built for the black sections (About,
// Contact). `neutral` is for a `bg-neutral-900` band — #171717 is within three
// values of #1a1a1a, so the pill disappears there and leaves a rotating arc of
// border floating on nothing. Used by the News carousel and the Portfolio
// pricing CTA, which both sit on that band.
//
// It separates by going LIGHTER than the band rather than darker. Both
// directions make the pill visible, but on a page that is otherwise black a
// button darker than its own panel reads as a hole in it; lighter reads as
// something sitting on top, which is what a button is.
//
// The two variables move together. `-subtle` is painted as an inset 1px ring
// just inside the gradient border, and it is what keeps that border reading as
// a hairline — left behind, it draws a dark line around the inside of the pill.
const TONE_STYLE = {
  dark: undefined,
  neutral: {
    "--shiny-cta-bg": "#262626",
    "--shiny-cta-bg-subtle": "#262626",
  },
};

// Shiny CTA button — adapted from https://21st.dev/community/components/aliimam/shiny-button
// Highlight recolored to brand blue (#2e8eff). Polymorphic: pass `href` to render
// a Next.js Link (navigation), otherwise it renders a <button>. Forwards the rest
// of the props (type, disabled, onClick, …) so it also works as a form submit.
export function ShinyButton({
  children,
  className = "",
  href,
  tone = "dark",
  style,
  ...props
}) {
  const classes = `shiny-cta ${className}`.trim();
  // Caller's own style wins, so a one-off can still override the tone.
  const styles = { ...TONE_STYLE[tone], ...style };

  if (href) {
    return (
      <Link href={href} className={classes} style={styles} {...props}>
        <span>{children}</span>
      </Link>
    );
  }

  return (
    <button className={classes} style={styles} {...props}>
      <span>{children}</span>
    </button>
  );
}
